import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { NotebookPen, Plus, Trash2, Check, Loader2 } from "lucide-react";

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const now = () => new Date().toISOString();

const NoteRow = ({ it, onChange, onDelete }) => (
  <div className="group flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-secondary/50" data-testid={`my-note-${it.id}`}>
    <Checkbox checked={it.done} onCheckedChange={(v) => onChange({ done: !!v, done_at: v ? now() : null })} data-testid={`my-note-check-${it.id}`} />
    <Input value={it.text} onChange={(e) => onChange({ text: e.target.value })} className={`h-8 border-transparent bg-transparent px-1 shadow-none focus-visible:border-border ${it.done ? "text-muted-foreground line-through" : ""}`} data-testid={`my-note-text-${it.id}`} />
    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 max-sm:opacity-100" onClick={onDelete} data-testid={`my-note-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
  </div>
);

// Lista personale delle modifiche future, salvata in automatico (visibile solo all'utente)
export const MyNotes = () => {
  const { data } = useQuery({ queryKey: ["my-notes"], queryFn: async () => (await api.get("/me/notes")).data, refetchOnWindowFocus: false });
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("saved");
  const timer = useRef(null);
  useEffect(() => { if (data && items === null) { setItems(data.items || []); } }, [data, items]);
  const save = useMutation({
    mutationFn: async (list) => (await api.put("/me/notes", { items: list })).data,
    onSuccess: () => setStatus("saved"),
    onError: () => setStatus("error"),
  });
  const update = (list, delay = 700) => {
    setItems(list);
    setStatus("pending");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save.mutate(list), delay);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const add = () => {
    if (!draft.trim()) return;
    update([{ id: newId(), text: draft.trim(), done: false, created_at: now() }, ...items], 0);
    setDraft("");
  };
  const list = items || [];
  const todo = list.filter((i) => !i.done);
  const done = list.filter((i) => i.done);
  const patch = (id, p) => update(list.map((i) => (i.id === id ? { ...i, ...p } : i)), "text" in p ? 700 : 0);
  const remove = (id) => update(list.filter((i) => i.id !== id), 0);
  return (
    <Card className="mt-6 p-6" data-testid="my-notes-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><NotebookPen className="h-4 w-4 text-primary" />Le mie note <span className="text-sm font-normal text-muted-foreground">modifiche future da fare · visibili solo a te</span></h3>
        <span className={`flex items-center gap-1 text-xs ${status === "error" ? "text-rose-400" : "text-muted-foreground"}`} data-testid="my-notes-status">
          {status === "pending" && <><Loader2 className="h-3 w-3 animate-spin" />Salvataggio…</>}
          {status === "saved" && <><Check className="h-3 w-3" />Salvato</>}
          {status === "error" && "Non salvato: riprova"}
        </span>
      </div>
      <form className="mb-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Nuova modifica da fare…" data-testid="my-notes-new-input" />
        <Button type="submit" disabled={!draft.trim()} data-testid="my-notes-add-btn"><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>
      </form>
      {items === null ? <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
        <>
          {todo.length === 0 && <p className="py-2 text-sm text-muted-foreground" data-testid="my-notes-empty">Nessuna modifica da fare.</p>}
          <div className="space-y-0.5" data-testid="my-notes-todo">{todo.map((it) => <NoteRow key={it.id} it={it} onChange={(p) => patch(it.id, p)} onDelete={() => remove(it.id)} />)}</div>
          {done.length > 0 && (
            <div className="mt-4 border-t border-border/60 pt-3" data-testid="my-notes-done">
              <p className="mb-1 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Fatte ({done.length})</p>
              {done.map((it) => <NoteRow key={it.id} it={it} onChange={(p) => patch(it.id, p)} onDelete={() => remove(it.id)} />)}
            </div>
          )}
        </>
      )}
    </Card>
  );
};
